import {
  type RentalManagerUserPropertyMultiplexWebApiHandler_caption_Args,
  type RentalManagerUserPropertyMultiplexWebApiHandler_caption_Options,
  useRentalManagerUserPropertyMultiplexWebApiHandler_caption,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import type { Client } from '@zg-rentals/http-client';
import type {
  ApiResponseErrorDto,
  UserPropertyMultiplexPhotoResponseWebDto,
} from '@zg-rentals/ts-rental-manager-api-client';

export type { UserPropertyMultiplexPhotoResponseWebDto };

const toApiResponseErrorDto = (error: unknown): ApiResponseErrorDto<unknown> | undefined => {
  if (!DatarouterError.is(error)) {
    return undefined;
  }

  return {
    code: error.code ?? '',
    message: error.message,
    data: error.errorDetails,
  };
};

/**
 * Runtime guard beyond compile-time args:
 * `getParams.multiplexBuildingSessionId` and `getParams.photoId` must be non-empty strings.
 */
const assertMultiplexPhotoCaptionMutationArgs = (
  args: RentalManagerUserPropertyMultiplexWebApiHandler_caption_Args,
): void => {
  const { getParams } = args;

  if (typeof getParams !== 'object' || getParams === null) {
    throw new TypeError('useMultiplexPhotoCaption: `getParams` is required (CaptionParams).');
  }

  const sessionId = getParams.multiplexBuildingSessionId;
  const photoId = getParams.photoId;

  if (typeof sessionId !== 'string' || sessionId.trim() === '') {
    throw new TypeError(
      'useMultiplexPhotoCaption: `getParams.multiplexBuildingSessionId` is required on every call (non-empty string).',
    );
  }

  if (typeof photoId !== 'string' || photoId.trim() === '') {
    throw new TypeError('useMultiplexPhotoCaption: `getParams.photoId` is required on every call (non-empty string).');
  }

  if (getParams.caption !== undefined && getParams.caption !== null && typeof getParams.caption !== 'string') {
    throw new TypeError('useMultiplexPhotoCaption: when set, `getParams.caption` must be a string.');
  }
};

export type UseMultiplexPhotoCaptionOptions = Omit<
  RentalManagerUserPropertyMultiplexWebApiHandler_caption_Options,
  'mutationFn'
> & {
  httpClient?: Client;
};

type BaseMultiplexPhotoCaptionMutation = ReturnType<typeof useRentalManagerUserPropertyMultiplexWebApiHandler_caption>;

export type UseMultiplexPhotoCaptionResult = Omit<
  BaseMultiplexPhotoCaptionMutation,
  'mutate' | 'mutateAsync' | 'data' | 'error' | 'isPending'
> & {
  mutate: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_caption_Args,
    options?: Parameters<BaseMultiplexPhotoCaptionMutation['mutate']>[1],
  ) => ReturnType<BaseMultiplexPhotoCaptionMutation['mutate']>;
  mutateAsync: (
    args: RentalManagerUserPropertyMultiplexWebApiHandler_caption_Args,
    options?: Parameters<BaseMultiplexPhotoCaptionMutation['mutateAsync']>[1],
  ) => ReturnType<BaseMultiplexPhotoCaptionMutation['mutateAsync']>;
  data: BaseMultiplexPhotoCaptionMutation['data'];
  error: BaseMultiplexPhotoCaptionMutation['error'];
  isPending: BaseMultiplexPhotoCaptionMutation['isPending'];
  errors: ApiResponseErrorDto<unknown> | undefined;
  success: boolean | undefined;
  errorMessage: UserPropertyMultiplexPhotoResponseWebDto['errorMessage'] | undefined;
  units: UserPropertyMultiplexPhotoResponseWebDto['units'] | undefined;
};

/**
 * Multiplex photo caption: wraps the generated `caption` mutation (POST `/api/web/v1/users/properties/multiplex/photos/caption`).
 *
 * Merges default `meta`: invalidates tag `multiplexBuildingSessionDetails` (plus any `meta.invalidates` you pass) and
 * sets `meta.context` for logging unless overridden.
 *
 * Exposes the usual mutation API (`mutate`, `mutateAsync`, `reset`, `data`, `error`, `isPending`, ...). Also returns
 * `errors` (normalized shape when the request throws), and `success`, `errorMessage`, and `units` from the last
 * successful `data`.
 *
 * **Calling the mutation:** Use `{ getParams: { multiplexBuildingSessionId, photoId, caption? } }`. Compile-time typing
 * applies to `mutate` / `mutateAsync`; runtime checks enforce non-empty session id and photo id strings.
 */
const useMultiplexPhotoCaption = (
  mutationOptions: UseMultiplexPhotoCaptionOptions = {},
): UseMultiplexPhotoCaptionResult => {
  const { meta: metaOverrides, ...restMutationOptions } = mutationOptions;

  const mutationResult = useRentalManagerUserPropertyMultiplexWebApiHandler_caption({
    ...restMutationOptions,
    meta: {
      ...metaOverrides,
      invalidates: ['multiplexBuildingSessionDetails', ...(metaOverrides?.invalidates ?? [])],
      context: {
        api: 'RentalManagerUserPropertyMultiplexWebApiHandler',
        details: 'multiplexBuildingSessionPhotoCaption',
        ...metaOverrides?.context,
      },
    },
  });

  const { data, error, isPending, mutate, mutateAsync, ...rest } = mutationResult;

  return {
    ...rest,
    mutate: (args, options) => {
      assertMultiplexPhotoCaptionMutationArgs(args);
      return mutate(args, options);
    },
    mutateAsync: (args, options) => {
      assertMultiplexPhotoCaptionMutationArgs(args);
      return mutateAsync(args, options);
    },
    data,
    error,
    isPending,
    errors: toApiResponseErrorDto(error),
    success: data?.success,
    errorMessage: data?.errorMessage,
    units: data?.units,
  };
};

export default useMultiplexPhotoCaption;
